import { Button, InlineAlert, Modal } from '@lego/connect-components-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormBuilder, { FormBuilderActions, SaveButton } from './FormBuilder';
import FormPreview from './FormPreview';
import type { useFormEditor } from './hooks';

type FormEditorModalProps = {
  editor: ReturnType<typeof useFormEditor>;
};

export const FormEditorModal = ({ editor }: FormEditorModalProps) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLElement>(null);
  const [showPreview, setShowPreview] = useState(false);

  const isOpen = editor.editorMode !== 'closed';

  useEffect(() => {
    if (!isOpen) setShowPreview(false);
  }, [isOpen]);

  const handlePreview = async () => {
    const success = await editor.handlePreview();
    if (success) setShowPreview(true);
  };

  const handleClose = () => {
    editor.closeEditor();
    setShowPreview(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      triggerRef={triggerRef}
      size="x-large"
      scrollBehavior="content"
    >
      <Modal.Title>
        {showPreview
          ? t('formPage.preview.title')
          : editor.isEditing
            ? t('formPage.dialog.editTitle', { name: editor.formState.name })
            : t('formPage.dialog.createTitle')}
      </Modal.Title>
      <Modal.Content>
        {editor.validationError && (
          <div className="mb-300">
            <InlineAlert variant="warning">
              <InlineAlert.Title>{editor.validationError}</InlineAlert.Title>
            </InlineAlert>
          </div>
        )}
        {showPreview ? (
          <FormPreview
            formState={editor.formState}
            selectedLabel={editor.selectedLabel}
            printers={editor.printers}
            previewResult={editor.previewResult}
          />
        ) : (
          <FormBuilder
            formState={editor.formState}
            isEditing={editor.isEditing}
            submitAttempted={editor.submitAttempted}
            groupOptions={editor.groupOptions}
            onGroupChange={editor.handleGroupChange}
            labelOptions={editor.labelOptions}
            labelRevisionOptions={editor.labelRevisionOptions}
            printerOptions={editor.printerOptions}
            selectedPrinterValues={editor.selectedPrinterValues}
            onFieldChange={editor.updateFormState}
            onPrinterChange={editor.handlePrinterChange}
            onVariableChange={editor.handleVariableChange}
            onRemoveVariable={editor.handleRemoveVariable}
            onAddVariable={editor.handleAddVariable}
          />
        )}
      </Modal.Content>
      <Modal.Footer>
        {showPreview ? (
          <div className="flex w-full items-center justify-between">
            <Button
              variant="primary"
              appearance="ghost"
              size="medium"
              onClick={() => setShowPreview(false)}
              iconName="arrow-left"
              iconPosition="start"
            >
              {t('formPage.preview.backToEditingMode')}
            </Button>
            <SaveButton
              isEditing={editor.isEditing}
              isSubmitting={editor.isSubmitting}
              onSave={editor.handleSave}
            />
          </div>
        ) : (
          <FormBuilderActions
            isEditing={editor.isEditing}
            isSubmitting={editor.isSubmitting}
            isPreviewValidating={editor.isPreviewValidating}
            hasUnsavedChanges={editor.hasUnsavedChanges}
            onSave={editor.handleSave}
            onPreview={handlePreview}
          />
        )}
      </Modal.Footer>
    </Modal>
  );
};
